import { useMemo, useState } from 'react';
import { DEFAULT_TREASURE_MAP_FILTERS } from '../utils/treasureMapFilters';

const DEFAULT_SOURCE_LAYERS = {
  location: true,
  donation: true,
  bottle_message: true,
};

function createInitialLayers() {
  return { ...DEFAULT_SOURCE_LAYERS };
}

function buildItemKey(item) {
  return `${item?.sourceType || 'item'}-${item?.id ?? 'unknown'}`;
}

export function useTreasureMapUi({ canAddLocation = false } = {}) {
  const [filters, setFilters] = useState({
    ...DEFAULT_TREASURE_MAP_FILTERS,
    layers: createInitialLayers(),
  });
  const [viewMode, setViewMode] = useState('map');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showAddLocationDrawer, setShowAddLocationDrawer] = useState(false);
  const [mapPickMode, setMapPickMode] = useState(false);
  const [selectedItemKey, setSelectedItemKey] = useState('');
  const [shouldFollowSelectedItem, setShouldFollowSelectedItem] = useState(false);

  const updateFilter = (key, value) => {
    setFilters((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const toggleBooleanFilter = (key) => {
    setFilters((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const toggleLayer = (key) => {
    if (!key) {
      return;
    }

    setFilters((previous) => ({
      ...previous,
      layers: {
        ...(previous.layers || {}),
        [key]: previous.layers?.[key] === false,
      },
    }));
  };

  const setMaxDistanceMeters = (value) => {
    const numericValue = Number(value);
    updateFilter(
      'maxDistanceMeters',
      Number.isFinite(numericValue) && numericValue > 0
        ? numericValue
        : DEFAULT_TREASURE_MAP_FILTERS.maxDistanceMeters
    );
  };

  const resetFilters = () => {
    setFilters({
      ...DEFAULT_TREASURE_MAP_FILTERS,
      layers: createInitialLayers(),
    });
  };

  const applyQuickFilter = (preset) => {
    switch (preset) {
      case 'loot-nearby':
        setFilters((previous) => ({
          ...previous,
          nearbyOnly: true,
          lootableOnly: true,
          activeCategory: 'all',
          layers: {
            ...createInitialLayers(),
            donation: false,
          },
        }));
        setViewMode('map');
        break;

      case 'donations':
        setFilters((previous) => ({
          ...previous,
          nearbyOnly: false,
          lootableOnly: false,
          activeCategory: 'all',
          layers: {
            ...createInitialLayers(),
            location: false,
            bottle_message: false,
            donation: true,
          },
        }));
        setViewMode('map');
        break;

      case 'messages':
        setFilters((previous) => ({
          ...previous,
          nearbyOnly: false,
          lootableOnly: false,
          activeCategory: 'all',
          layers: {
            ...createInitialLayers(),
            location: false,
            donation: false,
            bottle_message: true,
          },
        }));
        setViewMode('list');
        break;

      case 'reset':
      default:
        resetFilters();
        break;
    }
  };

  const isDonationsOnlyFilterActive = useMemo(() => {
    const layers = filters.layers || {};
    return layers.donation !== false && layers.location === false && layers.bottle_message === false;
  }, [filters.layers]);

  const toggleDonationsOnly = () => {
    if (isDonationsOnlyFilterActive) {
      setFilters((previous) => ({
        ...previous,
        nearbyOnly: false,
        lootableOnly: false,
        activeCategory: 'all',
        layers: createInitialLayers(),
      }));
      setViewMode('map');
      return;
    }

    setFilters((previous) => ({
      ...previous,
      nearbyOnly: false,
      lootableOnly: false,
      activeCategory: 'all',
      layers: {
        ...createInitialLayers(),
        location: false,
        bottle_message: false,
        donation: true,
      },
    }));
    setViewMode('map');
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;

    if (String(filters.query || '').trim() !== '') count += 1;
    if (filters.activeCategory && filters.activeCategory !== 'all') count += 1;
    if (filters.nearbyOnly) count += 1;
    if (filters.lootableOnly) count += 1;
    if (Number(filters.maxDistanceMeters) !== Number(DEFAULT_TREASURE_MAP_FILTERS.maxDistanceMeters)) count += 1;

    const disabledLayers = Object.values(filters.layers || {}).filter((value) => value === false).length;
    count += disabledLayers;

    return count;
  }, [filters]);

  return {
    filters,
    viewMode,
    showFilterPanel,
    showAddLocationDrawer,
    mapPickMode,
    selectedItemKey,
    shouldFollowSelectedItem,
    activeFilterCount,
    isDonationsOnlyFilterActive,
    setViewMode,
    setShowFilterPanel,
    setShowAddLocationDrawer,
    setMapPickMode,
    setSelectedItemKey,
    clearSelectedItem: () => {
      setSelectedItemKey('');
      setShouldFollowSelectedItem(false);
    },
    selectItem: (item, options = {}) => {
      const { followMap = true } = options || {};
      setSelectedItemKey(buildItemKey(item));
      setShouldFollowSelectedItem(Boolean(followMap));
    },
    setQuery: (value) => updateFilter('query', value),
    setActiveCategory: (value) => updateFilter('activeCategory', value),
    toggleNearbyOnly: () => toggleBooleanFilter('nearbyOnly'),
    toggleLootableOnly: () => toggleBooleanFilter('lootableOnly'),
    toggleDonationsOnly,
    toggleLayer,
    setMaxDistanceMeters,
    resetFilters,
    applyQuickFilter,
    openAddLocationDrawer: () => {
      if (!canAddLocation) {
        return;
      }
      setShowAddLocationDrawer(true);
    },
    closeAddLocationDrawer: () => {
      setShowAddLocationDrawer(false);
      setMapPickMode(false);
    },
    toggleAddLocationForm: () => {
      if (!canAddLocation) {
        return;
      }
      setShowAddLocationDrawer((previous) => !previous);
      setMapPickMode(false);
    },
    startMapPickMode: () => {
      if (!canAddLocation) {
        return;
      }
      setViewMode('map');
      setShowAddLocationDrawer(true);
      setMapPickMode(true);
    },
    stopMapPickMode: () => setMapPickMode(false),
    toggleMapPickMode: () => {
      if (!canAddLocation) {
        return;
      }
      setViewMode('map');
      setShowAddLocationDrawer(true);
      setMapPickMode((previous) => !previous);
    },
  };
}

export default useTreasureMapUi;
